/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import * as d3 from 'd3';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumbs } from '@blueprintjs/core';
import { Node } from './treeNode';
import { useColorHierarchy } from './useColorHeirarchy';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { Sizes } from 'styles/variables-slykit';
import { sendUsageAction } from 'utilities/usageStats';

function getValue(d) {
	// d3.sum() uses the node's value plus the value of it's children
	// Return 0 for any parent node and 'value' for leaf nodes
	// Sqrt the value so that smaller things show up better
	// Also, sometimes the server has (d.children = []) which should be have a value of a leaf node
	return d.children && d.children.length > 0 ? 0 : Math.sqrt(d.value) ?? 0;
}

export const TREE_MAP_FILTER_KEY = 'tree map';

const leafRatio = 1.2; // higher means wider

function getPathFromNode(node) {
	const path = node
		.ancestors()
		.reverse()
		.map((d) => d.data.key)
		.filter((x) => !!x)
		.join('.');
	return path;
}

/**
 * Treemap widget
 *
 * @param dimensions
 * @param data
 * @param backgroundColor
 * @returns {*}
 * @constructor
 */
export function TreemapWidget({
	dimensions,
	data,
	treemapProperty,
	focusedNodeKey,
	setFocusedNode: setFocusedNodeReact,
}) {
	const treemapWrapperRef = useRef(null);

	// Create d3 tree
	const { root, treeMapDimensions, leaves, tree } = useMemo(() => {
		const root = d3.hierarchy({ children: data.entries }).sum(getValue);

		// @ryan: is this okay?
		const treeMapDimensions =
			treemapWrapperRef.current != null
				? {
						height: treemapWrapperRef.current.clientHeight,
						width: treemapWrapperRef.current.clientWidth,
				  }
				: dimensions;

		const tree = d3
			.treemap()
			.size([treeMapDimensions.width / leafRatio, treeMapDimensions.height])
			// Secondary treemap needs to have no padding
			// NOTE: it might be worth-while to recalculate at each level. Doing so will likely make animations very difficult to maintain
			.padding(0)
			// .padding((d) => d.depth <= 1 ? Math.pow(1 / 3, d.depth) * 5 : 0)
			// .paddingOuter((d) => (d.depth > 2 ? 0 : 5))
			.round(true)
			.tile(d3.treemapResquarify.ratio(1))(root);

		const leaves = tree.descendants().reverse();
		return {
			root,
			treeMapDimensions,
			leaves,
			tree,
		};
	}, [data.entries, dimensions, treemapWrapperRef.current?.clientHeight]);

	// Full data for the node in focus
	const focusedNode = leaves.find((x) => x.data.key === focusedNodeKey);

	// Create a filter based on the focused node
	const { dispatch } = useContext(WorkspaceContext);
	const setFocusedNode = useCallback(
		(key) => {
			if (key !== root.data.key) {
				const newNode = leaves.find((x) => x.data.key === key);
				const path = getPathFromNode(newNode);
				// Safety if
				if (path) {
					dispatch(
						WorkspaceActionCreator.updateQueryString(TREE_MAP_FILTER_KEY, {
							[treemapProperty]: {
								$sw: path,
							},
						})
					);
					sendUsageAction('TreeMapZoomed', path);
				}
			} else {
				dispatch(WorkspaceActionCreator.updateQueryString(TREE_MAP_FILTER_KEY, null));
			}
			setFocusedNodeReact(key);
		},
		[dispatch, leaves, root.data.key, setFocusedNodeReact, treemapProperty]
	);

	// Clear Treemap filter if the component un-mounts
	useEffect(() => {
		return () => dispatch(WorkspaceActionCreator.updateQueryString(TREE_MAP_FILTER_KEY, null));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Set focusedNode on load or reset it if the focused node cannot be found.
	useEffect(() => {
		if (!focusedNodeKey || !focusedNode) {
			// default to root
			setFocusedNode(tree.data.key);
		}
	}, [focusedNode, focusedNodeKey, setFocusedNode, setFocusedNodeReact, tree]);

	// Only update the topNode after we get the new data back.
	// (it makes animations better). This also causes a problem when reloading
	// Dimensions are there for fix an error for breaking the rules of hooks
	const [topNode, setTopNode] = useState(null);
	useEffect(() => {
		setTopNode(focusedNode);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dimensions.width, dimensions.height]);

	// Reset topNode (like if the widget is reset)
	useEffect(() => {
		if (!focusedNode?.data?.key) {
			setTopNode(null);
		}
	}, [focusedNode]);

	// Calculate scales for zooming into the focusedNode
	let x0 = 0;
	let x1 = treeMapDimensions.width / leafRatio; // dividing by ratio prevents un-necessary animation
	let y0 = 0;
	let y1 = treeMapDimensions.height;
	if (topNode) {
		x0 = topNode.x0;
		x1 = topNode.x1;
		y0 = topNode.y0;
		y1 = topNode.y1;
	}
	const xScale = d3.scaleLinear().domain([x0, x1]).range([0, treeMapDimensions.width]);
	const yScale = d3.scaleLinear().domain([y0, y1]).range([0, treeMapDimensions.height]);
	const focusedDepth = topNode?.depth ?? 0;

	// Deterministically generate colors
	const colorsByKey = useColorHierarchy(root.children ?? []);

	// Hide un-needed leaves
	const filteredLeaves = leaves.filter(
		(n) => n.depth === focusedDepth || n.depth === focusedDepth + 1 || n.depth === focusedDepth + 2
	);

	// Generate breadcrumb data
	const breadcrumbItems =
		focusedNode
			?.ancestors()
			.reverse()
			.map((node) => {
				return {
					text: node.depth === 0 ? 'All' : node.data.key,
					onClick: () => setFocusedNode(node.data.key),
				};
			}) ?? [];

	return (
		<div css={treemapRootStyles}>
			<div css={breadcrumbWrapperStyles}>
				<div css={breadcrumbLabel}>Zoom:</div>
				<Breadcrumbs items={breadcrumbItems} />
			</div>

			<div css={treemapOutsideWrapperStyles} ref={treemapWrapperRef}>
				<div
					cy-test="treemap-data"
					width={treeMapDimensions.width}
					height={treeMapDimensions.height}
					css={treemapInsideWrapperStyles}
				>
					{filteredLeaves.map((node) => {
						const {
							data: { key },
							depth,
						} = node;
						return (
							<Node
								// Ensure a unique key across all nodes in the tree
								key={`${key}-${depth}`}
								node={node}
								xScale={xScale}
								yScale={yScale}
								focusedDepth={focusedDepth}
								focusedNodeKey={focusedNodeKey}
								colorsByKey={colorsByKey}
								setFocusedNode={setFocusedNode}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

const treemapRootStyles = css`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	width: 100%;
`;

const treemapOutsideWrapperStyles = css`
	position: relative;
	flex: 1 1 auto;
`;
const treemapInsideWrapperStyles = css`
	position: absolute;
	overflow: hidden;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
`;
const breadcrumbWrapperStyles = css`
	display: flex;
	align-items: center;
	flex: 0 0 auto;
	margin: 0 ${Sizes.sm} ${Sizes.sm};
`;

const breadcrumbLabel = css`
	color: #696969;
	padding-right: 5px;
	font-size: 16px;
`;
